import { useMemo } from 'react';

import { EMPTY_ARRAY } from '@e-socna/empty';

import type { ThirdPartiesCmp_SomeOrganization } from '../queries/some-organizations.query';

import { useFindAllInternalOrganizationsQuery } from './useFindAllInternalOrganizationsQuery.hook';
import { useFindAllExternalOrganizationsQuery } from './useFindAllExternalOrganizationsQuery.hook';

interface UnionResult {
  readonly data:
    | Readonly<{
        findSomeOrganizations: readonly ThirdPartiesCmp_SomeOrganization[];
      }>
    | undefined;
  readonly isLoading: boolean;
  readonly isSuccess: boolean;
}

export const useFindAllOrganizationsQuery = (enabled = true): UnionResult => {
  const externalQuery = useFindAllExternalOrganizationsQuery(enabled);
  const internalQuery = useFindAllInternalOrganizationsQuery(enabled);

  const memoizedResult = useMemo(() => {
    const externalData = externalQuery.data?.findSomeOrganizations;
    const internalData = internalQuery.data?.findSomeOrganizations;

    const data =
      externalData || internalData
        ? {
            findSomeOrganizations: [
              ...(externalData ?? EMPTY_ARRAY),
              ...(internalData ?? EMPTY_ARRAY),
            ],
          }
        : undefined;

    const isLoading = externalQuery.isLoading || internalQuery.isLoading;
    const isSuccess = externalQuery.isSuccess && internalQuery.isSuccess;

    return { data, isLoading, isSuccess };
  }, [
    externalQuery.data?.findSomeOrganizations,
    externalQuery.isLoading,
    externalQuery.isSuccess,
    internalQuery.data?.findSomeOrganizations,
    internalQuery.isLoading,
    internalQuery.isSuccess,
  ]);

  return memoizedResult;
};
